import { useState, useRef } from 'react';
import { AnimatePresence, m, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  ChevronLeft,
  ChevronDown,
  Bell,
  Search,
  Moon,
  Sun,
  LogOut,
  User,
  Settings as SettingsIcon,
  RotateCcw,
  Sparkles,
  Lock,
} from 'lucide-react';
import { UserButton, useClerk } from '@clerk/clerk-react';
import { useClerkGate } from '@/hooks/useClerkGate';
import { useRole } from '@/contexts/RoleContext';
import { useViewAs } from '@/contexts/ViewAsContext';
import type { GlobalRole } from '@aica/core/rbac';
import { usePermissions } from '@/contexts/PermissionContext';
import { useLayout } from '@/contexts/LayoutContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useOnboardingTour } from '@/components/onboarding';
import { useUnifiedShellView } from '@/components/dashboards/unified/useUnifiedShellView';
import { NotificationsDropdown } from './NotificationsDropdown';
import { LanguageToggle } from '@/components/theme/LanguageToggle';
import { HelpMenu } from './HelpMenu';
import { SIDEBAR_SECTIONS, navItemAccess, type NavItem } from './sidebar-config';
import { toProductPackageKey } from '@aica/core';
import { minPlanLabel, PACKAGE_LABEL } from '@/lib/product-package-label';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useTrialOpened } from '@/hooks/useTrialOpened';
import { PRO_TRIAL_PLAN } from '@/lib/pro-trial';
import { useAssistantDrawerSafe } from '@/contexts/AssistantDrawerContext';
import { useOrgTopic } from '@/hooks/assistant/useOrgTopic';
import { useTier } from '@/contexts/TierContext';
import { useHasUnsavedWork } from '@/components/settings/UnsavedChangesGuard';
import { ConfirmDialog } from '@/components/modals';

export interface AppSidebarProps {
  notificationCount?: number;
  alertsBadgeCount?: number;
  onCommandPaletteOpen?: () => void;
  className?: string;
}

export function AppSidebar({
  notificationCount = 0,
  alertsBadgeCount = 0,
  onCommandPaletteOpen,
  className,
}: AppSidebarProps) {
  // Optimistic active state — reads `location.pathname`, which TanStack Router
  // updates immediately on click (before loaders resolve). Replaces useMatchRoute,
  // which was tied to resolvedLocation and only flipped after fetches completed.
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const { getVisibleScreens, isLoading, globalRole, orgs, selectedOrg, selectOrg } =
    useRole();
  const { isOwner } = usePermissions();
  // useClerkGate instead of Clerk's useUser — avoids sidebar re-render on
  // every ~50s token refresh (see useClerkGate.ts).
  const { name: userName, email: userEmail } = useClerkGate();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { sidebarCollapsed, toggleSidebarCollapsed } = useLayout();
  const { resolvedTheme, setTheme } = useTheme();
  const { t } = useTranslation();
  const { viewAsRole, setViewAsRole } = useViewAs();
  const prefersReducedMotion = useReducedMotion();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isOrgMenuOpen, setIsOrgMenuOpen] = useState(false);
  /**
   * Switching organization is not a navigation — `selectOrg` flips context,
   * `isOrgSwitching` swaps the screen for a skeleton, and any form below
   * unmounts with whatever was typed into it. `UnsavedChangesGuard`'s router
   * blocker cannot see that, so the confirmation has to happen here, at the
   * click. Holds the pending org id while the question is on screen.
   */
  const hasUnsavedWork = useHasUnsavedWork();
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    analytics: true,
    development: true,
    // 'configuration' stays expanded: it is the entry point to analysis setup,
    // and collapsed-by-default made the hub undiscoverable (user report 2026-07-18)
  });
  const notificationsButtonRef = useRef<HTMLButtonElement>(null);

  const tourType =
    globalRole === 'agent'
      ? 'agent'
      : globalRole === 'supervisor'
        ? 'supervisor'
        : 'admin';
  const { startTour } = useOnboardingTour(tourType);
  const unifiedShell = useUnifiedShellView() !== null;
  const { isEnabled: isFeatureEnabled, gate } = useFeatureFlags();
  const trialOpened = useTrialOpened();
  // AI assistant (#765, §8.0): the header entry opens the panel about the
  // record currently open; without one it is disabled with the reason. A
  // subject whose access may ask about nothing (#1677) has no entry at all —
  // the provider's `available`, not a button disabled for good.
  const assistantDrawer = useAssistantDrawerSafe();
  const orgTopic = useOrgTopic();

  // Real subscription tier (was hardcoded "Pro Plan"). useTier falls back to a
  // context default ('free') when no TierProvider is mounted, so this is safe.
  const { tier } = useTier();
  const planLabel = `${PACKAGE_LABEL[toProductPackageKey(tier)]} Plan`;

  const visibleScreens = getVisibleScreens();

  const isItemActive = (item: NavItem) => {
    const targets = [item.href, ...(item.matchPatterns ?? [])];
    return targets.some((target) => {
      if (target === '/') return currentPath === '/';
      return currentPath === target || currentPath.startsWith(target + '/');
    });
  };

  const getBadgeCount = (badge: NavItem['badge']): number | null => {
    switch (badge) {
      case 'records':
        return notificationCount > 0 ? notificationCount : null;
      case 'alerts':
        return alertsBadgeCount > 0 ? alertsBadgeCount : null;
      default:
        return null;
    }
  };

  const itemAccess = (item: NavItem) =>
    item.featureFlag
      ? navItemAccess(gate(item.featureFlag), isFeatureEnabled(item.featureFlag))
      : 'visible';

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon;
    const isActive = isItemActive(item);
    const label = t(item.labelKey);
    const badgeCount = getBadgeCount(item.badge);
    // A feature above the plan (#1897): shown with a lock and the plan it is
    // sold from, and still a link — the screen behind it says what it is.
    const locked = itemAccess(item) === 'locked';
    const plan = locked && item.featureFlag ? minPlanLabel(item.featureFlag) : null;
    const lockedLabel = locked
      ? plan
        ? t('nav.lockedPlan', { label, plan })
        : t('nav.locked', { label })
      : undefined;
    // Open only because of the Pro trial (#2816): «Pro · trial» in the lock's
    // place — the trial's plan, not the feature's floor.
    const trialPlan =
      !locked && item.featureFlag && trialOpened(item.featureFlag)
        ? PRO_TRIAL_PLAN
        : null;
    const trialLabel = trialPlan
      ? t('proTrial.navLabel', { label, plan: trialPlan })
      : undefined;
    const accessLabel = lockedLabel ?? trialLabel;

    return (
      <li key={item.id}>
        <Link
          to={item.href}
          data-testid={`sidebar-nav-${item.id}`}
          className={cn(
            'group relative flex w-full items-center gap-3 rounded-md px-3 py-2 min-h-[40px]',
            'text-sm transition-all duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            isActive
              ? 'bg-bg-highlight text-primary border-l-[3px] border-l-primary pl-[calc(12px-3px)]'
              : locked
                ? 'text-fg-muted hover:bg-bg-elevated hover:text-fg'
                : 'text-fg-secondary hover:bg-bg-elevated hover:text-fg',
            sidebarCollapsed && 'justify-center px-2'
          )}
          aria-current={isActive ? 'page' : undefined}
          aria-label={accessLabel}
          data-locked={locked ? 'true' : undefined}
          title={sidebarCollapsed ? (accessLabel ?? label) : undefined}
        >
          <span className="relative shrink-0">
            <Icon
              className={cn(
                'h-[18px] w-[18px] opacity-70',
                isActive && 'opacity-100 text-primary'
              )}
              aria-hidden="true"
            />
            {locked && sidebarCollapsed && (
              <Lock
                className="absolute -bottom-1 -right-1 h-3 w-3 text-fg-muted"
                aria-hidden="true"
                data-testid={`sidebar-lock-${item.id}`}
              />
            )}
          </span>
          {!sidebarCollapsed && (
            <span className="flex-1 text-left truncate">{label}</span>
          )}
          {!sidebarCollapsed && locked && (
            <span
              className="flex items-center gap-1 text-xs text-fg-subtle"
              data-testid={`sidebar-lock-${item.id}`}
            >
              <Lock className="h-3 w-3" aria-hidden="true" />
              {plan}
            </span>
          )}
          {!sidebarCollapsed && trialPlan && (
            <span
              className="text-xs text-info-fg"
              data-testid={`sidebar-trial-${item.id}`}
            >
              {t('proTrial.label', { plan: trialPlan })}
            </span>
          )}
          {!sidebarCollapsed && badgeCount != null && badgeCount > 0 && (
            <span
              className={cn(
                'flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5',
                'text-xs font-semibold tabular-nums',
                item.badge === 'records' ? 'bg-primary text-bg-void' : 'bg-danger text-fg'
              )}
            >
              {badgeCount}
            </span>
          )}
        </Link>
      </li>
    );
  };

  return (
    <aside
      data-testid="sidebar"
      aria-label={t('dashboard:appSidebar.applicationSidebar')}
      className={cn(
        'flex h-full flex-col border-r border-border bg-bg',
        'transition-all duration-200 ease-out',
        'motion-reduce:transition-none',
        sidebarCollapsed ? 'w-16' : 'w-64',
        className
      )}
    >
      {/* Header: Logo + Collapse */}
      <div
        className={cn(
          'flex h-14 items-center border-b border-border px-4 shrink-0',
          sidebarCollapsed ? 'justify-center' : 'justify-between'
        )}
      >
        {!sidebarCollapsed ? (
          <Link
            to="/dashboard"
            className="flex items-center gap-2 group"
            data-testid="sidebar-logo"
            aria-label={t('dashboard:appSidebar.goToDashboard')}
          >
            <div className="h-7 w-7 rounded-md border border-border bg-bg-surface flex items-center justify-center shrink-0 relative">
              <div className="absolute inset-1 rounded-full border border-primary" />
              <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            </div>
            <span className="text-xl font-bold text-fg tracking-wider">Aelo</span>
          </Link>
        ) : (
          <Link
            to="/dashboard"
            className="h-7 w-7 rounded-md border border-border bg-bg-surface flex items-center justify-center relative"
            data-testid="sidebar-logo"
            title={t('common:nav.dashboard')}
          >
            <div className="absolute inset-1 rounded-full border border-primary" />
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
          </Link>
        )}
        <button
          data-testid="sidebar-collapse-button"
          onClick={toggleSidebarCollapsed}
          className={cn(
            'rounded-md p-1.5 text-fg-muted transition-all duration-150',
            'hover:bg-bg-elevated hover:text-fg',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            sidebarCollapsed &&
              'absolute -right-3 top-4 z-[101] rounded-full bg-bg-surface border border-border shadow-sm w-6 h-6 p-0 flex items-center justify-center'
          )}
          aria-label={
            sidebarCollapsed
              ? t('dashboard:appSidebar.expandSidebar')
              : t('dashboard:appSidebar.collapseSidebar')
          }
        >
          <ChevronLeft
            className={cn(
              'h-4 w-4 transition-transform duration-200',
              sidebarCollapsed && 'rotate-180'
            )}
          />
        </button>
      </div>

      {/* Org Switcher — rich component per prototype */}
      {!sidebarCollapsed && (
        <div className="px-4 pt-3 pb-0 shrink-0">
          {orgs.length > 1 ? (
            <div className="relative">
              <button
                data-testid="sidebar-org-selector"
                onClick={() => setIsOrgMenuOpen(!isOrgMenuOpen)}
                className="flex w-full items-center gap-3 rounded-md px-2 py-2 hover:bg-bg-elevated transition-colors"
              >
                <div className="h-8 w-8 rounded-md bg-gradient-to-br from-primary to-accent flex items-center justify-center text-sm font-semibold text-bg-void shrink-0">
                  {(selectedOrg?.name || 'A').slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-medium text-fg truncate">
                    {selectedOrg?.name || 'Organization'}
                  </div>
                  <div className="text-xs text-fg-muted">{planLabel}</div>
                </div>
                <ChevronDown className="h-3 w-3 shrink-0 text-fg-faint" />
              </button>
              {isOrgMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-[199]"
                    onClick={() => setIsOrgMenuOpen(false)}
                  />
                  <div className="absolute top-full left-0 right-0 mt-1 z-[200] rounded-lg border border-border bg-bg-elevated shadow-lg py-1">
                    {orgs.map((org) => (
                      <button
                        key={org.id}
                        onClick={() => {
                          setIsOrgMenuOpen(false);
                          if (hasUnsavedWork && org.id !== selectedOrg?.id) {
                            setPendingOrgId(org.id);
                            return;
                          }
                          selectOrg(org.id);
                        }}
                        className={cn(
                          'w-full text-left px-3 py-1.5 text-sm transition-colors hover:bg-bg-surface',
                          selectedOrg?.id === org.id
                            ? 'text-primary font-medium'
                            : 'text-fg'
                        )}
                      >
                        {org.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 px-2 py-2">
              <div className="h-8 w-8 rounded-md bg-gradient-to-br from-primary to-accent flex items-center justify-center text-sm font-semibold text-bg-void shrink-0">
                {(selectedOrg?.name || 'A').slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-fg truncate">
                  {selectedOrg?.name || 'Organization'}
                </div>
                <div className="text-xs text-fg-muted">{planLabel}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CmdK Trigger — full-width button per prototype */}
      {!sidebarCollapsed && isFeatureEnabled('enableCommandPalette') && (
        <div className="px-4 pt-3 shrink-0">
          <button
            data-tour="sidebar-search"
            onClick={onCommandPaletteOpen}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-3 py-2',
              'border border-bg-accent text-fg-muted text-sm',
              'hover:border-fg-faint hover:text-fg-secondary transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            )}
            aria-label={t('dashboard:appSidebar.searchShortcut')}
          >
            <Search className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1 text-left">{t('common:common.search')}...</span>
            <kbd className="ml-auto px-1.5 py-0.5 border border-bg-accent rounded text-xs font-mono bg-bg-elevated">
              ⌘K
            </kbd>
          </button>
        </div>
      )}

      {/* Role Switcher — pill tabs per prototype */}
      {!sidebarCollapsed && isOwner && (
        <div className="px-4 pt-2 shrink-0">
          {/* flex-wrap: five localized labels need ~300px against ~220px of
              sidebar width — without wrapping the tail pills bleed out of the
              aside and get painted over by the page content. The first pill is
              the platform owner's own view: its full name is too long for the
              strip, so it takes a short label of its own. */}
          <div
            className="flex flex-wrap gap-1 bg-bg-surface p-[3px] rounded-md"
            data-testid="sidebar-view-as-selector"
            role="tablist"
            aria-label={t('dashboard:appSidebar.roleSwitcher')}
          >
            {(
              [
                { value: '', labelKey: 'common:ownerMode.platformPill' },
                { value: 'client', labelKey: 'common:roles.client' },
                { value: 'admin', labelKey: 'common:roles.admin' },
                { value: 'supervisor', labelKey: 'common:roles.supervisor' },
                { value: 'agent', labelKey: 'common:roles.agent' },
              ] as const
            ).map((role) => (
              <button
                key={role.value}
                role="tab"
                aria-selected={(!viewAsRole && !role.value) || viewAsRole === role.value}
                onClick={() =>
                  setViewAsRole(role.value ? (role.value as GlobalRole) : null)
                }
                className={cn(
                  'flex-1 px-2 py-1 rounded text-xs transition-colors cursor-pointer',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  (!viewAsRole && !role.value) || viewAsRole === role.value
                    ? 'bg-bg-elevated text-fg font-medium'
                    : 'text-fg-muted hover:text-fg-secondary'
                )}
              >
                {t(role.labelKey)}
              </button>
            ))}
          </div>
          {/* Preview new-user Onboarding (owner-only, read-only — no DB writes) */}
          <button
            onClick={() => {
              // Full reload — SPA navigation tears down MainAppLayout (and with it
              // ClerkAuthProvider) while lazy-mounted dashboards are still rendering,
              // which trips `useAuth must be used within ClerkAuthProvider`.
              window.location.assign('/onboarding?preview=1');
            }}
            className={cn(
              'mt-1.5 w-full flex items-center justify-center gap-1.5 px-2 py-1 rounded text-xs',
              'text-fg-muted hover:text-fg hover:bg-bg-elevated transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            )}
            title={t('dashboard:appSidebar.previewOnboardingHint')}
          >
            <RotateCcw className="h-3 w-3" />
            {t('dashboard:appSidebar.previewOnboarding')}
          </button>
          {/* Restart the guided tour (Driver.js) — clears localStorage flag */}
          <button
            onClick={() => {
              const role = viewAsRole || 'admin';
              localStorage.removeItem(`aica-onboarding-${role}-seen`);
              window.location.reload();
            }}
            className={cn(
              'mt-1 w-full flex items-center justify-center gap-1.5 px-2 py-1 rounded text-xs',
              'text-fg-subtle hover:text-fg hover:bg-bg-elevated transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            )}
            title={t('dashboard:appSidebar.restartTourHint')}
          >
            {t('dashboard:appSidebar.restartTour')}
          </button>
        </div>
      )}

      {/* Scrollable Navigation */}
      <nav
        className="flex-1 overflow-y-auto overflow-x-hidden py-2 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent"
        aria-label={t('dashboard:appSidebar.mainNavigation')}
        data-testid="sidebar-navigation"
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-8" role="status">
            <div className="text-sm text-fg-muted animate-pulse">
              {t('common:common.loading')}
            </div>
          </div>
        ) : (
          SIDEBAR_SECTIONS.map((section) => {
            // Role-based section filtering
            if (
              section.roles &&
              !section.roles.some((r) => (r === 'owner' ? isOwner : globalRole === r))
            ) {
              return null;
            }

            // Filter items by visible screens, feature flags, and roles
            const visibleItems = section.items.filter((item) => {
              if (!visibleScreens.includes(item.id)) return false;
              if (itemAccess(item) === 'hidden') return false;
              if (
                item.roles &&
                !item.roles.some((r) => (r === 'owner' ? isOwner : globalRole === r))
              )
                return false;
              return true;
            });
            if (visibleItems.length === 0) return null;

            const isCollapsed = collapsedSections[section.id] ?? false;
            const sectionLabel = section.labelKey ? t(section.labelKey) : '';

            return (
              <div key={section.id} className="mb-1">
                {!sidebarCollapsed && sectionLabel && (
                  <button
                    type="button"
                    onClick={() =>
                      section.collapsible &&
                      setCollapsedSections((prev) => ({
                        ...prev,
                        [section.id]: !prev[section.id],
                      }))
                    }
                    className={cn(
                      'flex w-full items-center gap-1 px-4 pt-4 pb-1 text-xs font-medium uppercase tracking-wider text-fg-muted',
                      section.collapsible &&
                        'cursor-pointer hover:text-fg transition-colors'
                    )}
                  >
                    <span>{sectionLabel}</span>
                    {section.collapsible && (
                      <ChevronDown
                        className={cn(
                          'h-3 w-3 transition-transform duration-200',
                          isCollapsed && '-rotate-90'
                        )}
                      />
                    )}
                  </button>
                )}

                {prefersReducedMotion ? (
                  !isCollapsed && (
                    <ul className="space-y-0.5 px-2" role="list">
                      {visibleItems.map(renderNavItem)}
                    </ul>
                  )
                ) : (
                  <AnimatePresence initial={false}>
                    {!isCollapsed && (
                      <m.ul
                        className="space-y-0.5 px-2 overflow-hidden"
                        role="list"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
                      >
                        {visibleItems.map(renderNavItem)}
                      </m.ul>
                    )}
                  </AnimatePresence>
                )}
              </div>
            );
          })
        )}
      </nav>

      {/* Static Footer */}
      <div className="border-t border-border shrink-0" data-testid="sidebar-footer">
        {/* Action Icons Row */}
        <div
          className={cn(
            'flex items-center gap-1 p-2',
            sidebarCollapsed ? 'flex-col' : 'px-3'
          )}
        >
          {/* Notifications */}
          {isFeatureEnabled('enableNotifications') && (
            <button
              ref={notificationsButtonRef}
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
              className={cn(
                'relative rounded-md p-1.5 text-fg-muted transition-colors',
                'hover:bg-bg-elevated hover:text-fg',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              )}
              aria-label={
                notificationCount > 0
                  ? `Notifications (${notificationCount} unread)`
                  : 'Notifications'
              }
              title="Notifications"
            >
              <Bell className="h-[14px] w-[14px]" />
              {notificationCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold leading-none text-bg">
                  {notificationCount > 99 ? '99+' : notificationCount}
                </span>
              )}
            </button>
          )}

          {/* Search (CmdK) */}
          {isFeatureEnabled('enableCommandPalette') && (
            <button
              onClick={onCommandPaletteOpen}
              className="rounded-md p-1.5 text-fg-muted hover:bg-bg-elevated hover:text-fg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('dashboard:appSidebar.searchShortcut')}
              title={t('dashboard:appSidebar.searchShortcut')}
            >
              <Search className="h-[14px] w-[14px]" />
            </button>
          )}

          {!sidebarCollapsed && <span className="flex-1" />}

          {assistantDrawer?.available && (
            <button
              type="button"
              // The provider already derives the record on screen as a topic,
              // name and all. Rebuilding it here would be a third construction
              // of one value — a third place for the sidebar, the record header
              // and the panel to call the same record something different,
              // which is the drift `label` exists to make impossible (#922).
              //
              // No record on screen: the whole base (§8.0), once it is released —
              // its page, where the filters are (#1357). A paid turn in flight
              // keeps the drawer path: `openAssistant` then reopens the running
              // conversation instead of moving off it (R6, code-review on #1374).
              //
              // A record the access may not ask about is no `screenTopic`
              // (the provider's call, #1677).
              onClick={() => {
                const { screenTopic, busy, topic } = assistantDrawer;
                if (screenTopic || busy) {
                  // While busy the argument only reopens the running turn.
                  const next = screenTopic ?? orgTopic ?? topic;
                  if (next) assistantDrawer.openAssistant(next);
                } else if (orgTopic) {
                  // The page closes the drawer on arrival — a navigation kept
                  // back by unsaved settings leaves it open (Codex P2, #1374).
                  // Already on the page there is no arrival: close it here.
                  if (currentPath === '/assistant') assistantDrawer.closeAssistant();
                  else void navigate({ to: '/assistant' });
                }
              }}
              disabled={
                !assistantDrawer.screenTopic && !orgTopic && !assistantDrawer.busy
              }
              className="rounded-md p-1.5 text-ai hover:bg-bg-elevated hover:text-ai-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:text-fg-muted disabled:cursor-not-allowed"
              aria-label={t('assistant:openButton')}
              title={
                assistantDrawer.screenTopic || orgTopic || assistantDrawer.busy
                  ? t('assistant:openButton')
                  : t('assistant:openButtonNoSubject')
              }
              data-testid="sidebar-assistant-button"
            >
              <Sparkles className="h-[14px] w-[14px]" />
            </button>
          )}

          {/* Theme Toggle */}
          <button
            onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            className="rounded-md p-1.5 text-fg-muted hover:bg-bg-elevated hover:text-fg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('dashboard:appSidebar.toggleTheme')}
            title={t('dashboard:appSidebar.toggleTheme')}
          >
            {resolvedTheme === 'dark' ? (
              <Moon className="h-[14px] w-[14px]" />
            ) : (
              <Sun className="h-[14px] w-[14px]" />
            )}
          </button>

          {/* Language Toggle */}
          <LanguageToggle
            buttonClassName="rounded-md p-1.5 text-fg-muted hover:bg-bg-elevated hover:text-fg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            iconClassName="h-[14px] w-[14px]"
          />

          {/* Help: guided tour + context-aware documentation */}
          <HelpMenu
            // The unified dashboard has no tour: the item would send the user to
            // a page where nothing starts (#2581).
            onStartTour={unifiedShell ? undefined : startTour}
            currentPath={currentPath}
            buttonClassName="rounded-md p-1.5 text-fg-muted hover:bg-bg-elevated hover:text-fg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            iconClassName="h-[14px] w-[14px]"
          />
        </div>

        {/* Separator between icons and user row */}
        <div className="border-b border-border mx-3" />

        {/* User Row */}
        <div
          className={cn(
            'px-3 py-2 relative',
            sidebarCollapsed && 'px-2 flex justify-center'
          )}
        >
          {!sidebarCollapsed ? (
            <>
              <button
                data-testid="sidebar-user-menu-button"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="w-full flex items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-bg-elevated transition-colors text-left"
              >
                <UserButton />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-fg truncate">
                    {userName || 'User'}
                  </div>
                  <div className="text-micro text-fg-muted truncate">
                    {globalRole ? t(`common:roles.${globalRole}`) : ''}
                    {selectedOrg ? ` · ${selectedOrg.name}` : ''}
                  </div>
                </div>
                <ChevronDown
                  className={cn(
                    'h-3.5 w-3.5 shrink-0 text-fg-muted transition-transform',
                    isUserMenuOpen && 'rotate-180'
                  )}
                  aria-hidden="true"
                />
              </button>

              {/* User Menu Dropdown */}
              {isUserMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-[199]"
                    onClick={() => setIsUserMenuOpen(false)}
                  />
                  <div
                    data-testid="sidebar-user-menu"
                    className="absolute bottom-full left-3 right-3 mb-1 z-[200] rounded-lg border border-border bg-bg-surface shadow-lg overflow-hidden"
                  >
                    {/* User info header */}
                    <div className="px-3 py-2 border-b border-border">
                      <div className="text-sm font-medium text-fg">
                        {userName || 'User'}
                      </div>
                      <div className="text-micro text-fg-muted">{userEmail || ''}</div>
                    </div>
                    {/* My Profile */}
                    <button
                      data-testid="sidebar-user-profile"
                      onClick={() => {
                        void navigate({ to: '/settings' });
                        setIsUserMenuOpen(false);
                      }}
                      className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-fg-secondary hover:bg-bg-elevated transition-colors"
                    >
                      <User className="h-3 w-3" />{' '}
                      {t('common:common.myProfile', 'My Profile')}
                    </button>
                    {/* Settings */}
                    <button
                      data-testid="sidebar-user-settings"
                      onClick={() => {
                        void navigate({ to: '/settings' });
                        setIsUserMenuOpen(false);
                      }}
                      className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-fg-secondary hover:bg-bg-elevated transition-colors"
                    >
                      <SettingsIcon className="h-3 w-3" /> {t('nav.settings', 'Settings')}
                    </button>
                    {/* Sign Out */}
                    <div className="border-t border-border mt-1">
                      <button
                        data-testid="sidebar-sign-out"
                        onClick={() => {
                          void signOut({ redirectUrl: '/sign-in' });
                        }}
                        className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-danger hover:bg-bg-elevated transition-colors"
                      >
                        <LogOut className="h-3 w-3" />{' '}
                        {t('common:common.signOut', 'Sign Out')}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <UserButton />
          )}
        </div>

        {/* Notifications Dropdown (portal) */}
        {isFeatureEnabled('enableNotifications') && (
          <NotificationsDropdown
            isOpen={isNotificationsOpen}
            onClose={() => setIsNotificationsOpen(false)}
            anchorRef={notificationsButtonRef}
          />
        )}

        {pendingOrgId && (
          <div data-testid="org-switch-unsaved-dialog">
            <ConfirmDialog
              isOpen
              variant="warning"
              title={t('common:modals.unsavedChanges', 'Unsaved changes')}
              message={t(
                'common:modals.unsavedChangesSwitchOrgMessage',
                'You have unsaved changes on this page. Switching organization now will discard them.'
              )}
              confirmText={t('common:modals.discardChanges', 'Discard changes')}
              cancelText={t('common:modals.keepEditing', 'Keep editing')}
              onConfirm={() => {
                const target = pendingOrgId;
                setPendingOrgId(null);
                selectOrg(target);
              }}
              onClose={() => setPendingOrgId(null)}
            />
          </div>
        )}
      </div>
    </aside>
  );
}
